import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cuid from 'cuid';

import { CodeKernelFooter } from './CodeKernelFooter';
import { LockBanner } from '../components/LockBanner';
import Users from '../components/Users';
import Worksheet from '../components/Worksheet';
import ShareDBDoc from '../components/ShareDBDoc';
import ColumnTitle from '../components/ColumnTitle';
import {
  getOrCreatePreviewWorksheet,
  getTemplateFromDocument,
  loadTemplate,
} from '../resources/worksheets';
import { ReactComponent as WorkbookIcon } from '../icons/icon-forum-workbook.svg';
import { ReactComponent as CodeWorkbookIcon } from '../icons/icon-forum-code-workbook.svg';
import RollupWorkbook from './RollupWorkbook';
import { ErrorBanner } from '../components/ErrorBanner';

const extractUuidFromURL = (url) => /((\w{4,12}-?)){5}/.exec(url)[0];

class Workbook extends Component {
  state = {
    error: null,
    ErrorClass: Error,
    worksheet: null,
    path: undefined,
    activeDocumentId: null,
    template: null,
  };

  async onLoad() {
    const { url, currentUser, id: passedId, templateId } = this.props;
    const isPreview = templateId;
    const sessionKey = currentUser.rollup;
    const id = passedId || (url && extractUuidFromURL(url));

    if (isPreview) {
      const template = await loadTemplate(templateId);
      // We use a random uuid to ensure a clean document on every load
      const { uuid } = await getOrCreatePreviewWorksheet(id, template);
      this.setState({ activeDocumentId: uuid, template });
    } else if (sessionKey) {
      const rollupId = `${sessionKey}-rollup-doc`;
      // We still rely on at least one worksheet UUID being passed
      // for a rollup document, and it can not just be used through the session key.
      const sampleDocumentId = id;
      const template = await getTemplateFromDocument(sampleDocumentId);
      await getOrCreatePreviewWorksheet(rollupId, template, sessionKey);
      this.setState({ activeDocumentId: rollupId, template });
    } else {
      const template = await getTemplateFromDocument(id);
      this.setState({ activeDocumentId: id, template });
    }
  }

  setPath = (p) => {
    this.setState({ path: p });
  };

  componentDidMount() {
    this.onLoad();
  }

  render() {
    const { error, ErrorClass, activeDocumentId, template, path } = this.state;
    const { currentUser, id, url, templateId, bannerMessage } = this.props;

    if (error) {
      throw new ErrorClass(error);
    }

    if (!id && !url && !templateId) {
      return null;
    }

    if (!activeDocumentId || !template) {
      // Waiting for template to be loaded and sharedb doc to be created
      return null;
    }

    return (
      <ShareDBDoc
        docId={activeDocumentId}
        currentUser={currentUser}
        noPresence={!!currentUser.noPresence}
        readOnly={!!currentUser.readOnly}
      >
        {({ doc, data, presence, readOnlyWorksheet, connectionStatus }) =>
          currentUser.rollup ? (
            <RollupWorkbook
              doc={doc}
              data={data}
              template={template}
              currentUser={currentUser}
            />
          ) : (
            <>
              {doc.data.preview && (
                <LockBanner
                  text="Previewing workbook"
                  deprecated={data.deprecated}
                />
              )}
              {(bannerMessage || readOnlyWorksheet || data.deprecated) &&
                !currentUser.timeline && (
                  <LockBanner
                    text={bannerMessage}
                    locked={readOnlyWorksheet}
                    deprecated={data.deprecated}
                  />
                )}
              <div className="page flex flex-column p5">
                {!currentUser.iframed && (
                  <ColumnTitle
                    columnTitle={
                      template.subtype === 'code-sled'
                        ? 'Forum Code Workbook'
                        : 'Forum Workbook'
                    }
                    columnIcon={
                      template.subtype === 'code-sled' ? (
                        <CodeWorkbookIcon />
                      ) : (
                        <WorkbookIcon />
                      )
                    }
                  >
                    <Users
                      presence={presence}
                      users={data.users}
                      currentUser={currentUser}
                      doc={doc}
                    />
                  </ColumnTitle>
                )}
                <ErrorBanner connectionStatus={connectionStatus} />
                <Worksheet
                  template={template}
                  doc={doc}
                  currentUser={currentUser}
                  readOnlyWorksheet={readOnlyWorksheet}
                  data={currentUser.timeline && data}
                  path={path}
                  setPath={this.setPath}
                />
              </div>
              {template.subtype === 'code-sled' && (
                <CodeKernelFooter
                  doc={doc}
                  worksheet={template}
                  kernelState={data.kernelState}
                  readOnlyWorksheet={!path && readOnlyWorksheet}
                  path={path}
                />
              )}
            </>
          )
        }
      </ShareDBDoc>
    );
  }
}

Workbook.propTypes = {
  currentUser: PropTypes.object.isRequired,
  templateId: PropTypes.string,
  url: PropTypes.string,
  id: PropTypes.string,
};

export default Workbook;
